import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { toast } from "sonner";
import {
  GraduationCap, PlusCircle, Trash, Copy, CircleNotch, Info, CheckCircle,
} from "@phosphor-icons/react";
import {
  createSchoolInvite, listSchoolInvites, revokeSchoolInvite,
} from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";

function isExpired(iso) {
  try {
    return new Date(iso) < new Date();
  } catch { return false; }
}

function CopyButton({ text, testid }) {
  const [ok, setOk] = useState(false);
  const click = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setOk(true);
      setTimeout(() => setOk(false), 1500);
    } catch { /* ignore */ }
  };
  return (
    <button
      onClick={click}
      data-testid={testid}
      className="inline-flex items-center gap-1 text-[11px] text-[#00FA9A] hover:underline"
    >
      {ok ? <><CheckCircle size={10} weight="fill" /> kopiert</> : <><Copy size={10} /> kopieren</>}
    </button>
  );
}

export default function SchoolAdmin() {
  const { user } = useAuth();
  const [invites, setInvites] = useState(null);
  const [label, setLabel] = useState("");
  const [teacherEmail, setTeacherEmail] = useState("");
  const [maxUses, setMaxUses] = useState(30);
  const [daysValid, setDaysValid] = useState(60);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try { setInvites(await listSchoolInvites()); }
    catch { setInvites([]); }
  };
  useEffect(() => { load(); }, []);

  if (!user) return null;
  if (!user.is_admin) {
    return (
      <div className="eid-card p-6 text-sm">
        <div className="font-medium">Nur Admins dürfen Schul-Codes verwalten.</div>
        <div className="text-zinc-400 mt-1">
          Du bist als <code>{user.email}</code> angemeldet.
        </div>
      </div>
    );
  }

  const origin = typeof window !== "undefined" ? window.location.origin : "https://bodenblick.com";

  const submit = async (e) => {
    e.preventDefault();
    if (!label.trim()) { toast.error("Label fehlt (z. B. Klassenname)"); return; }
    setSaving(true);
    try {
      await createSchoolInvite({
        label: label.trim(),
        teacher_email: teacherEmail.trim() || null,
        max_uses: Number(maxUses),
        days_valid: Number(daysValid),
      });
      toast.success("Code erstellt");
      setLabel(""); setTeacherEmail("");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Konnte Code nicht erstellen");
    } finally { setSaving(false); }
  };

  const revoke = async (id, labelTxt) => {
    if (!window.confirm(`Code „${labelTxt}" wirklich deaktivieren?`)) return;
    await revokeSchoolInvite(id);
    toast.success("Code deaktiviert");
    load();
  };

  return (
    <div className="space-y-6" data-testid="school-admin-page">
      <Helmet><title>Schul-Codes · Admin · bodenblick.com</title></Helmet>

      <div className="eid-card p-6 md:p-8" data-testid="school-admin-hero">
        <div className="flex items-center gap-2">
          <GraduationCap size={22} className="text-[#00FA9A]" weight="fill" />
          <div className="eid-label">Admin · Schulmodus</div>
        </div>
        <h1 className="mt-2 text-3xl md:text-4xl font-medium tracking-tight">
          Codes für Klassen & Kurse
        </h1>
        <p className="mt-3 text-sm md:text-base text-zinc-400 max-w-2xl leading-relaxed">
          Erzeug einen 6-stelligen Code pro Klasse. Schüler:innen öffnen {" "}
          <code className="bg-white/5 px-1 rounded">{origin}/school</code>, tippen den
          Code + Vornamen ein und sind sofort drin — read-only, anonym, kostenlos.
        </p>
      </div>

      <form onSubmit={submit} className="eid-card p-5 space-y-3" data-testid="school-admin-form">
        <div className="eid-label">Neuer Code</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          <input
            value={label} onChange={(e) => setLabel(e.target.value)}
            placeholder="Klasse / Kurs *"
            data-testid="school-admin-label"
            className="border border-white/10 rounded-sm px-3 py-2 text-sm"
          />
          <input
            value={teacherEmail} onChange={(e) => setTeacherEmail(e.target.value)}
            placeholder="Lehrer:innen-Email (optional)"
            data-testid="school-admin-teacher-email"
            className="border border-white/10 rounded-sm px-3 py-2 text-sm"
          />
        </div>
        <div className="flex items-center gap-3">
          <label className="text-xs text-zinc-400">Max. Nutzer</label>
          <input
            type="number" min={1} max={500}
            value={maxUses} onChange={(e) => setMaxUses(e.target.value)}
            data-testid="school-admin-max-uses"
            className="w-20 border border-white/10 rounded-sm px-2 py-1 text-sm font-mono"
          />
          <label className="text-xs text-zinc-400 ml-4">Gültig (Tage)</label>
          <input
            type="number" min={1} max={365}
            value={daysValid} onChange={(e) => setDaysValid(e.target.value)}
            data-testid="school-admin-days-valid"
            className="w-20 border border-white/10 rounded-sm px-2 py-1 text-sm font-mono"
          />
          <div className="flex-1" />
          <button
            type="submit" disabled={saving}
            data-testid="school-admin-submit"
            className="bg-[#00FA9A] text-black hover:bg-[#33FBAE] disabled:opacity-60 rounded-full px-4 py-2 text-sm flex items-center gap-1"
          >
            {saving ? <CircleNotch size={12} className="animate-spin" /> : <PlusCircle size={12} />}
            Code erstellen
          </button>
        </div>
      </form>

      {invites === null && (
        <div className="eid-card p-5"><CircleNotch size={14} className="animate-spin inline" /> Lade …</div>
      )}

      {invites && invites.length === 0 && (
        <div className="eid-card p-6 text-center text-sm text-zinc-400" data-testid="school-admin-empty">
          Noch keine Codes — leg oben den ersten an.
        </div>
      )}

      {invites && invites.length > 0 && (
        <div className="eid-card overflow-hidden" data-testid="school-admin-list">
          <table className="w-full text-sm">
            <thead className="bg-white/5 text-[10px] uppercase tracking-wider text-zinc-400">
              <tr>
                <th className="p-2 text-left">Code</th>
                <th className="p-2 text-left">Label</th>
                <th className="p-2 text-right">Nutzer</th>
                <th className="p-2 text-left">Gültig bis</th>
                <th className="p-2 text-left">Status</th>
                <th className="p-2"></th>
              </tr>
            </thead>
            <tbody>
              {invites.map((i) => {
                const expired = isExpired(i.expires_at);
                const full = i.used_count >= i.max_uses;
                const ok = i.active && !expired && !full;
                return (
                  <tr
                    key={i.id}
                    data-testid={`school-admin-row-${i.id}`}
                    className="border-t border-white/10"
                  >
                    <td className="p-2 font-mono font-medium text-[#00FA9A]">
                      {i.code}
                      <div className="mt-0.5">
                        <CopyButton
                          testid={`school-admin-copy-${i.id}`}
                          text={`${origin}/school  —  Code: ${i.code}`}
                        />
                      </div>
                    </td>
                    <td className="p-2">{i.label}</td>
                    <td className="p-2 text-right font-mono">
                      {i.used_count} / {i.max_uses}
                    </td>
                    <td className="p-2 text-zinc-400 font-mono text-[11px]">
                      {(i.expires_at || "").slice(0, 10)}
                    </td>
                    <td className="p-2">
                      {!i.active && <span className="text-[10px] text-zinc-500">deaktiviert</span>}
                      {i.active && expired && <span className="text-[10px] text-[#FF7A45]">abgelaufen</span>}
                      {i.active && !expired && full && <span className="text-[10px] text-[#FFC24B]">voll</span>}
                      {ok && <span className="text-[10px] text-[#00FA9A]">aktiv</span>}
                    </td>
                    <td className="p-2 text-right">
                      {i.active && (
                        <button
                          onClick={() => revoke(i.id, i.label)}
                          data-testid={`school-admin-revoke-${i.id}`}
                          className="text-[11px] text-[#FF7A45] hover:bg-[#FF5500]/10 rounded px-2 py-1"
                        >
                          <Trash size={11} className="inline -mt-0.5" /> deaktivieren
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <div className="eid-card p-4 text-xs text-zinc-400 flex items-start gap-2">
        <Info size={12} className="text-[#00FA9A] shrink-0 mt-0.5" />
        <span>
          Jeder Schüler-Zugang ist ein separater anonymer Account mit
          read-only Rechten. Persönliche Daten werden nicht gespeichert —
          nur der Vorname wird im Navigationsbereich angezeigt.
        </span>
      </div>
    </div>
  );
}
